import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { PERSONAS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, CheckCircle2, Trash2, Plus, RefreshCw, Pencil, Wand2 } from "lucide-react";

const RHYTHMS = ["night_owl", "early_bird", "balanced"];
const GENDERS = ["female", "male", "non-binary"];
const GEOS = ["US", "UK"];

const emptyForm = {
  name: "", age: 24, gender: "female", geo: "US", language: "en-US",
  interests: [], bio: "", tone: "", daily_rhythm: "balanced", quirks: [],
  avatar_url: "", approved: false,
};

const AVATARS = [
  "https://images.unsplash.com/photo-1772371272141-0fbd644b65c4?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  "https://images.unsplash.com/photo-1740252117070-7aa2955b25f8?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
];

export default function Personas() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openManual, setOpenManual] = useState(false);
  const [openAi, setOpenAi] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  // AI wizard
  const [brief, setBrief] = useState({ age_range: "22-28", geo: "US", interests: ["crypto", "trading"], gender: "female", notes: "" });
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setList(await api.personas()); }
    catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openManualNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, avatar_url: AVATARS[Math.floor(Math.random() * AVATARS.length)] });
    setOpenManual(true);
  };
  const openEditPersona = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || "",
      age: p.age ?? 24,
      gender: p.gender || "female",
      geo: p.geo || "US",
      language: p.language || "en-US",
      interests: p.interests || [],
      bio: p.bio || "",
      tone: p.tone || "",
      daily_rhythm: p.daily_rhythm || "balanced",
      quirks: p.quirks || [],
      avatar_url: p.avatar_url || AVATARS[0],
      approved: !!p.approved,
    });
    setOpenManual(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Имя обязательно");
    const payload = {
      ...form,
      age: Number(form.age) || 20,
      interests: Array.isArray(form.interests) ? form.interests : (form.interests || "").split(",").map((s) => s.trim()).filter(Boolean),
      quirks: Array.isArray(form.quirks) ? form.quirks : (form.quirks || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.updatePersona(editing, payload);
      else await api.createPersona(payload);
      toast.success(t.personas.saved);
      setOpenManual(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch { toast.error(t.common.saveFailed); }
  };

  // AI flow
  const openAiWizard = () => {
    setDraft(null);
    setOpenAi(true);
  };
  const gen = async () => {
    setBusy(true); setDraft(null);
    try {
      const res = await api.generatePersona({ ...brief, interests: (brief.interests || []).filter(Boolean) });
      setDraft(res.draft);
      toast.success("Черновик готов");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.slice?.(0, 120) || t.common.failed);
    } finally { setBusy(false); }
  };
  const approve = async () => {
    if (!draft) return;
    try {
      await api.createPersona({
        ...draft,
        approved: true,
        interests: Array.isArray(draft.interests) ? draft.interests : [],
        quirks: Array.isArray(draft.quirks) ? draft.quirks : [],
      });
      toast.success(t.personas.saved);
      setOpenAi(false); setDraft(null); load();
    } catch { toast.error(t.common.saveFailed); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.deletePersona(id); load(); }
    catch { toast.error(t.common.failed); }
  };

  const aiRefresh = async (id) => {
    setRefreshingId(id);
    try {
      await api.aiRefreshPersona(id);
      toast.success(t.personas.aiRefreshDone);
      load();
    } catch {
      toast.error(t.personas.aiRefreshFailed);
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div data-testid={PERSONAS.root}>
      <PageHeader
        title={t.personas.title}
        description={t.personas.description}
        actions={
          <div className="flex gap-2">
            <button
              data-testid={PERSONAS.createBtn}
              onClick={openManualNew}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 text-slate-700 text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" /> {t.personas.new}
            </button>
            <button
              onClick={openAiWizard}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              <Wand2 className="h-4 w-4" /> {t.personas.aiWizard}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Sparkles className="h-10 w-10 text-emerald-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.personas.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.personas.emptyHint}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((p) => (
            <div key={p.id} className="card-soft p-5">
              <div className="flex items-start gap-3">
                <img src={p.avatar_url} alt="" className="h-14 w-14 rounded-2xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-semibold text-slate-900 truncate">{p.name}</div>
                    {p.approved && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                        {t.personas.approved}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {p.age} · {t.personas.gender[p.gender] || p.gender} · {p.geo} · {p.language}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    data-testid={PERSONAS.aiRefreshBtn(p.id)}
                    onClick={() => aiRefresh(p.id)}
                    disabled={refreshingId === p.id}
                    title={t.personas.aiRefresh}
                    className="text-slate-400 hover:text-emerald-600 transition-colors p-1 disabled:opacity-40"
                  >
                    {refreshingId === p.id
                      ? <RefreshCw className="h-4 w-4 animate-spin" />
                      : <Wand2 className="h-4 w-4" />}
                  </button>
                  <button
                    data-testid={PERSONAS.editBtn(p.id)}
                    onClick={() => openEditPersona(p)}
                    className="text-slate-400 hover:text-emerald-600 transition-colors p-1"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={PERSONAS.deleteBtn(p.id)}
                    onClick={() => del(p.id)}
                    className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-2">{p.bio}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p.interests || []).slice(0, 5).map((tag) => (
                  <span key={tag} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-3">
                Ритм: <span className="text-slate-700 font-medium">{t.personas.rhythm[p.daily_rhythm] || p.daily_rhythm}</span>
                {p.tone && <> · Тон: {p.tone}</>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manual create/edit dialog */}
      <Dialog open={openManual} onOpenChange={setOpenManual}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t.personas.edit : t.personas.new}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex items-center gap-3">
              <img src={form.avatar_url || AVATARS[0]} alt="" className="h-14 w-14 rounded-2xl object-cover" />
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.avatarUrl}</Label>
                <Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="mt-1 font-mono text-xs" />
              </div>
              <div className="flex gap-1">
                {AVATARS.map((a) => (
                  <button
                    key={a}
                    onClick={() => setForm({ ...form, avatar_url: a })}
                    className={`h-8 w-8 rounded-full overflow-hidden border-2 ${form.avatar_url === a ? "border-emerald-500" : "border-transparent"}`}
                  >
                    <img src={a} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.name}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.age}</Label>
              <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.gender}</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GENDERS.map((g) => <SelectItem key={g} value={g}>{t.personas.gender[g]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.geo}</Label>
              <Select
                value={form.geo}
                onValueChange={(v) => setForm({ ...form, geo: v, language: v === "US" ? "en-US" : "en-GB" })}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GEOS.map((g) => <SelectItem key={g} value={g}>{t.geoOptions[g]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.language}</Label>
              <Input value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.rhythm}</Label>
              <Select value={form.daily_rhythm} onValueChange={(v) => setForm({ ...form, daily_rhythm: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RHYTHMS.map((r) => <SelectItem key={r} value={r}>{t.personas.rhythm[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.bio}</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.tone}</Label>
              <Input value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.interests}</Label>
              <Input
                value={Array.isArray(form.interests) ? form.interests.join(", ") : form.interests}
                onChange={(e) => setForm({ ...form, interests: e.target.value.split(",").map((s) => s.trim()) })}
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.quirks}</Label>
              <Input
                value={Array.isArray(form.quirks) ? form.quirks.join(", ") : form.quirks}
                onChange={(e) => setForm({ ...form, quirks: e.target.value.split(",").map((s) => s.trim()) })}
                className="mt-1"
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                id="approved"
                type="checkbox"
                checked={!!form.approved}
                onChange={(e) => setForm({ ...form, approved: e.target.checked })}
                className="h-4 w-4 accent-emerald-500"
              />
              <Label htmlFor="approved" className="cursor-pointer text-sm">одобрена</Label>
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={PERSONAS.saveBtn}
              onClick={submit}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              {editing ? t.common.saveChanges : t.common.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI wizard */}
      <Dialog open={openAi} onOpenChange={setOpenAi}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{t.personas.aiWizard}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Диапазон возраста</Label>
              <Input value={brief.age_range} onChange={(e) => setBrief({ ...brief, age_range: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.gender}</Label>
              <Select value={brief.gender} onValueChange={(v) => setBrief({ ...brief, gender: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GENDERS.map((g) => <SelectItem key={g} value={g}>{t.personas.gender[g]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.geo}</Label>
              <Select value={brief.geo} onValueChange={(v) => setBrief({ ...brief, geo: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GEOS.map((g) => <SelectItem key={g} value={g}>{t.geoOptions[g]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.interests}</Label>
              <Input
                value={(brief.interests || []).join(", ")}
                onChange={(e) => setBrief({ ...brief, interests: e.target.value.split(",").map((s) => s.trim()) })}
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.personas.fields.notes}</Label>
              <Textarea value={brief.notes} onChange={(e) => setBrief({ ...brief, notes: e.target.value })} className="mt-1" placeholder="e.g. студентка, сухой юмор, любит мемы" />
            </div>
          </div>

          <button
            data-testid={PERSONAS.aiGenerateBtn}
            onClick={gen}
            disabled={busy}
            className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? t.common.generating : t.personas.generate}
          </button>

          {draft && (
            <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex items-start gap-3">
                <img src={draft.avatar_url} alt="" className="h-14 w-14 rounded-2xl object-cover" />
                <div className="flex-1">
                  <div className="font-display font-semibold text-slate-900">{draft.name}</div>
                  <div className="text-xs text-slate-500">
                    {draft.age} · {t.personas.gender[draft.gender] || draft.gender} · {draft.geo} · {draft.language}
                  </div>
                  <p className="text-sm text-slate-700 mt-2">{draft.bio}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(draft.interests || []).map((x) => (
                      <span key={x} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200">{x}</span>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2">
                    Ритм: {t.personas.rhythm[draft.daily_rhythm] || draft.daily_rhythm} · Тон: {draft.tone}
                  </div>
                  {Array.isArray(draft.quirks) && draft.quirks.length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-1">Особенности: {draft.quirks.join(" · ")}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <button
              disabled={!draft}
              onClick={approve}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-sm font-medium transition-colors"
            >
              <CheckCircle2 className="h-4 w-4" /> {t.personas.approve}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
